import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  ColDef,
  ColumnEvent,
  ColumnResizedEvent,
  ColumnState,
  convertColumnState,
  GridApi,
  GridState,
  ModelUpdatedEvent,
  RowClassParams,
  SortChangedEvent,
} from 'ag-grid-community'
import { GridReadyEvent } from 'ag-grid-enterprise'
import {
  getOrderedColumnsFromState,
  isColumnStateCompatible,
  isPersistableColumnResize,
  isProgrammaticColumnEvent,
} from '@/utils/ag-grid-table-helpers.ts'
import { useVisibilityAwareInterval } from '@/common/hooks/use-visibility-aware-interval.ts'
import { useIsMobile } from '@/common/hooks/use-is-mobile.ts'
import { useTutorial } from '@/contexts/use-tutorial.ts'

// Default no-rows overlay template. Kept here so both the grids and the error
// path (showGridError) share a single source of truth. showGridError swaps the
// template to an error message; showGridLoading/finishGridLoading restore this
// default so a prior error message never lingers on later empty results.
export const DEFAULT_NO_ROWS_TEMPLATE = '<span>No rows to show</span>'

// ─── Defaults ───

/**
 * Builds the shared defaultColDef including a custom "Reset Columns" menu item
 * that clears persisted column state from localStorage.
 */
export function useGridDefaults(
  storageKey: string,
  gridApiRef: React.RefObject<GridApi | null>,
  columnStateResetRef: React.MutableRefObject<boolean>
): ColDef {
  return useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      initialWidth: 200,
      filter: false,
      mainMenuItems: (params) => {
        return params.defaultItems.map((item) => {
          if (item === 'resetColumns') {
            return {
              name: 'Reset Columns',
              action: () => {
                columnStateResetRef.current = true
                localStorage.removeItem(storageKey)
                gridApiRef.current?.resetColumnState()
                // resetColumnState only restores layout; clear active filters too
                // so "Reset Columns" returns the grid to a fully default state.
                gridApiRef.current?.setFilterModel(null)
              },
            }
          }
          return item
        })
      },
    }),
    [storageKey, gridApiRef, columnStateResetRef]
  )
}

// ─── Auto-Refresh ───

/**
 * Wraps useVisibilityAwareInterval for grid auto-refresh.
 * Normalizes interval values: 0 / undefined → null (disabled).
 */
export function useGridAutoRefresh(
  refreshFn: () => void,
  interval: number | null | undefined
): void {
  const normalizedInterval = interval && interval > 0 ? interval : null

  useVisibilityAwareInterval(refreshFn, normalizedInterval)
}

// ─── Row Highlight ───

// Fallback used only if the CSS variable cannot be read (e.g. before mount).
// The real value lives in idea-list-table.scss (--idea-highlight-duration).
const HIGHLIGHT_DURATION_FALLBACK_MS = 1500

// Read the highlight animation duration defined in CSS so the state-clearing
// timer stays in sync with the animation. CSS is the single source of truth.
function readHighlightDurationMs(selector: string): number {
  const el = document.querySelector(selector)
  if (!el) return HIGHLIGHT_DURATION_FALLBACK_MS

  const raw = getComputedStyle(el)
    .getPropertyValue('--idea-highlight-duration')
    .trim()
  if (!raw) return HIGHLIGHT_DURATION_FALLBACK_MS

  // Supports both `1500ms` and `1.5s` notations.
  const ms = raw.endsWith('ms')
    ? parseFloat(raw)
    : raw.endsWith('s')
      ? parseFloat(raw) * 1000
      : parseFloat(raw)

  return Number.isFinite(ms) ? ms : HIGHLIGHT_DURATION_FALLBACK_MS
}

interface UseHighlightRowOptions {
  /**
   * CSS selector of the element that defines `--idea-highlight-duration`.
   * Defaults to `.idea-list-table`, which both the idea and position tables use.
   */
  selector?: string
  /**
   * Multiplier applied to the CSS animation duration when deciding how long to
   * wait before clearing the highlight source. Defaults to 2.
   */
  durationMultiplier?: number
}

/**
 * Shared row-highlight behavior for grids navigated to via router `state`.
 *
 * - Returns `isHighlightedRow`, a predicate for the `ag-row--hightlighted`
 *   `rowClassRules` entry, based on `location.state.activeIdeaId`.
 * - Clears `location.state` once the highlight animation has finished so the
 *   highlight does not re-trigger on refresh/re-render.
 */
export function useHighlightRow(options?: UseHighlightRowOptions) {
  const location = useLocation()
  const navigate = useNavigate()
  const selector = options?.selector ?? '.idea-list-table'
  const durationMultiplier = options?.durationMultiplier ?? 2

  const activeIdeaId = (location.state as { activeIdeaId?: string } | null)
    ?.activeIdeaId

  useEffect(() => {
    if (!activeIdeaId) return

    const timer = setTimeout(
      () => {
        navigate(location.pathname, { replace: true, state: null })
      },
      readHighlightDurationMs(selector) * durationMultiplier
    )

    return () => clearTimeout(timer)
  }, [activeIdeaId, location.pathname, navigate, selector, durationMultiplier])

  const isHighlightedRow = (params: RowClassParams) =>
    activeIdeaId
      ? activeIdeaId === params.data?.id || activeIdeaId === params.data?.ideaId
      : false

  return { isHighlightedRow }
}

// ─── Column State ───

/**
 * Manages column state persistence via localStorage.
 * Handles save/restore/reset and resolves final column definitions
 * considering mobile reorder and saved state.
 */
export function useGridColumnState(
  storageKey: string,
  gridApiRef: React.RefObject<GridApi | null>,
  columnDefs: ColDef[]
) {
  // Tracks whether a column reset was requested.
  // Consumed by resolveColumnDefs to skip saved-state restoration on the next render.
  const columnStateResetRef = useRef(false)

  // Detect stale (old UI) persisted state directly from its content instead of a
  // separate version tag: if any current column is missing from the saved
  // column state, the state predates the current layout (new columns were
  // added) and is discarded so the table rebuilds from current defaults.
  // Runs synchronously so `initialState`/`resolveColumnDefs` read the cleaned value.
  const isSavedStateCompatible = isColumnStateCompatible(
    localStorage.getItem(storageKey),
    columnDefs
  )
  if (!isSavedStateCompatible) {
    localStorage.removeItem(storageKey)
  }

  const initialState = useMemo<GridState | undefined>(() => {
    // An incompatible state has already been cleared above; nothing to restore.
    if (!isSavedStateCompatible) return undefined
    const savedState = localStorage.getItem(storageKey)
    if (!savedState) return undefined

    try {
      const columnState = JSON.parse(savedState) as ColumnState[]
      return {
        ...convertColumnState(columnState),
        partialColumnState: true,
      }
    } catch {
      return undefined
    }
    // isSavedStateCompatible is derived synchronously each render; safe to omit.
  }, [storageKey])

  const saveColumnState = useCallback(() => {
    if (!gridApiRef.current) return
    const state = gridApiRef.current.getColumnState()
    localStorage.setItem(storageKey, JSON.stringify(state))
  }, [storageKey, gridApiRef])

  const clearColumnState = useCallback(() => {
    localStorage.removeItem(storageKey)
    gridApiRef.current?.resetColumnState()
  }, [storageKey, gridApiRef])

  /**
   * Returns the final ColDef[] considering:
   * 1. Mobile-first reorder (if isMobile + mobileColumnOrder provided)
   * 2. Post-reset: return raw columnDefs without saved state
   * 3. Normal: restore column order from localStorage
   *
   * NOTE: This function reads and clears `columnStateResetRef.current` as a
   * one-shot flag set by clearColumnState/useGridDefaults reset action.
   * The mutation is intentional — it ensures reset only skips saved-state
   * restoration for a single render cycle.
   */
  const resolveColumnDefs = useCallback(
    (
      columnDefs: ColDef[],
      isMobile: boolean,
      mobileColumnOrder?: string[]
    ): ColDef[] => {
      // Mobile column reorder
      if (isMobile && mobileColumnOrder && mobileColumnOrder.length > 0) {
        // Match by field first, falling back to colId so columns without a
        // `field` (e.g. an actions/edit-button column) can still be reordered.
        const getColKey = (col: ColDef) => col.field ?? col.colId ?? ''
        const firstCols = mobileColumnOrder
          .map((key) => columnDefs.find((col) => getColKey(col) === key))
          .filter(Boolean) as ColDef[]
        const restCols = columnDefs.filter(
          (col) => !mobileColumnOrder.includes(getColKey(col))
        )
        return [...firstCols, ...restCols]
      }

      // After a reset, return base columns without applying saved state
      if (columnStateResetRef.current) {
        columnStateResetRef.current = false
        return columnDefs
      }

      // Attempt to restore column order from saved state
      const savedState = localStorage.getItem(storageKey)
      const orderedColumns = getOrderedColumnsFromState(
        savedState,
        columnDefs,
        gridApiRef.current
      )
      if (orderedColumns) return orderedColumns

      return columnDefs
    },
    [storageKey, gridApiRef]
  )

  return useMemo(
    () => ({
      initialState,
      saveColumnState,
      clearColumnState,
      resolveColumnDefs,
      columnStateResetRef,
    }),
    [initialState, saveColumnState, clearColumnState, resolveColumnDefs]
  )
}

// ─── Events ───

export interface UseGridEventsOptions {
  gridApiRef: React.RefObject<GridApi | null>
  isInitialLoad: boolean
  saveColumnState: () => void
  onColumnEvent?: (
    eventType: string,
    event: ColumnEvent | SortChangedEvent
  ) => void
}

/**
 * Provides unified column event handlers that:
 * - Filter out programmatic events (api, gridInitializing, etc.)
 * - Skip events during initial load (checked via ref to avoid stale closures)
 * - Persist column state on user-initiated changes
 * - Forward events to the analytics callback
 */
export function useGridEvents({
  gridApiRef,
  isInitialLoad,
  saveColumnState,
  onColumnEvent,
}: UseGridEventsOptions) {
  const handleColumnChanged = useCallback(
    (event: ColumnEvent) => {
      if (isProgrammaticColumnEvent(event.source)) return
      if (isInitialLoad || !gridApiRef.current) return
      saveColumnState()
      onColumnEvent?.(event.type, event)
    },
    [isInitialLoad, saveColumnState, onColumnEvent, gridApiRef]
  )

  const handleColumnResized = useCallback(
    (event: ColumnResizedEvent) => {
      // Persist user-initiated resizes: manual drag and the column menu's
      // "Autosize This Column" / "Autosize All Columns" actions.
      if (isInitialLoad || !gridApiRef.current) return
      if (!isPersistableColumnResize(event)) return
      saveColumnState()
      onColumnEvent?.('columnResized', event)
    },
    [isInitialLoad, saveColumnState, onColumnEvent, gridApiRef]
  )

  const handleSortChanged = useCallback(
    (event: SortChangedEvent) => {
      if (isProgrammaticColumnEvent(event.source)) return
      if (isInitialLoad || !gridApiRef.current) return
      saveColumnState()
      onColumnEvent?.('sortChanged', event)
    },
    [isInitialLoad, saveColumnState, onColumnEvent, gridApiRef]
  )

  return {
    handleColumnChanged,
    handleColumnResized,
    handleSortChanged,
  }
}

// ─── Shared Grid Setting ───

export interface UseGridSharedOptions {
  columnDefs: ColDef[]
  storageKey: string
  mobileColumnOrder?: string[]
  mobileBreakpoint?: number
  tutorialColId?: string
  onColumnEvent?: (
    eventType: string,
    event: ColumnEvent | SortChangedEvent
  ) => void
}

export interface GridRequestContext {
  requestEpoch: number
}

/**
 * Consolidates the boilerplate shared by InfiniteGrid and SSRMGrid:
 * - grid api ref, mobile detection, initial-load + first-page tracking state
 * - column-state persistence, event handlers and defaultColDef wiring
 * - resolved column defs (mobile reorder + saved state)
 * - initial grid state restoration + tutorial ref
 * - loading / no-rows overlay control
 *
 * Each grid keeps its own datasource, refresh and imperative-handle logic,
 * since those differ between the infinite and server-side row models.
 */
export function useGridShared({
  columnDefs,
  storageKey,
  mobileColumnOrder,
  mobileBreakpoint = 768,
  tutorialColId,
  onColumnEvent,
}: UseGridSharedOptions) {
  const gridApiRef = useRef<GridApi | null>(null)
  const isMobile = useIsMobile(mobileBreakpoint)
  // Tracks whether the first data fetch has completed.
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true)
  // Tracks whether a first-page fetch is in flight. Kept in a ref so the
  // onModelUpdated handler always reads the latest value without re-binding.
  const isLoadingRef = useRef(false)
  // Tracks whether the most recent fetch failed, so the no-rows overlay can be
  // suppressed in favor of an error message instead of a misleading "No rows".
  const hadErrorRef = useRef(false)
  const requestEpochRef = useRef(0)
  const requestContextRef = useRef<GridRequestContext>({ requestEpoch: 0 })
  const { setRef } = useTutorial()

  const beginRequestEpoch = useCallback(() => {
    requestEpochRef.current += 1
    requestContextRef.current = {
      requestEpoch: requestEpochRef.current,
    }
    gridApiRef.current?.setGridOption('context', requestContextRef.current)
    return requestEpochRef.current
  }, [])

  const getCurrentRequestEpoch = useCallback(() => requestEpochRef.current, [])

  const isCurrentRequestEpoch = useCallback(
    (epoch: number) => requestEpochRef.current === epoch,
    []
  )

  const {
    initialState,
    saveColumnState,
    resolveColumnDefs,
    columnStateResetRef,
  } = useGridColumnState(storageKey, gridApiRef, columnDefs)

  const { handleColumnChanged, handleColumnResized, handleSortChanged } =
    useGridEvents({
      gridApiRef,
      isInitialLoad,
      saveColumnState,
      onColumnEvent,
    })

  const defaultColDef = useGridDefaults(
    storageKey,
    gridApiRef,
    columnStateResetRef
  )

  const resolvedColumnDefs = useMemo(
    () => resolveColumnDefs(columnDefs, isMobile, mobileColumnOrder),
    [resolveColumnDefs, columnDefs, isMobile, mobileColumnOrder]
  )

  const handleGridReady = useCallback(
    (params: GridReadyEvent) => {
      gridApiRef.current = params.api
      params.api.setGridOption('context', requestContextRef.current)

      // Set tutorial ref on the specified column header (if configured).
      // Delay needed to ensure ag-grid has finished rendering the header DOM.
      if (tutorialColId) {
        setTimeout(() => {
          const headerCell = document.querySelector(
            `[col-id="${tutorialColId}"]`
          )
          if (headerCell) {
            setRef('tableColumn', { current: headerCell as HTMLElement })
          }
        }, 100)
      }
    },
    [setRef, tutorialColId]
  )

  // Shows/hides the no-rows overlay based on the actual displayed row count.
  // Reliable once the datasource has reported its lastRow (0 when empty).
  // Skipped while the last fetch is in an error state so the error overlay
  // (set via showGridError) is not overwritten by a "No rows" message.
  const syncNoRowsOverlay = useCallback((api: GridApi) => {
    if (hadErrorRef.current) return
    if (api.getDisplayedRowCount() === 0) {
      api.showNoRowsOverlay()
    } else {
      api.setGridOption('loading', false)
      // Rows exist: hide any lingering no-rows overlay. Without this a
      // transient empty state during a sort/refresh (store briefly rebuilt to
      // 0 rows) would leave the "No rows" overlay stuck over real data.
      api.hideOverlay()
    }
  }, [])

  // Show the loading overlay for a first-page fetch. Toggled imperatively via
  // setGridOption (the showLoadingOverlay() API is deprecated in v32+).
  const showGridLoading = useCallback((epoch: number) => {
    if (requestEpochRef.current !== epoch) return
    isLoadingRef.current = true
    hadErrorRef.current = false
    const api = gridApiRef.current
    if (!api) return
    // Restore the default no-rows template in case a previous fetch failed and
    // swapped it to an error message via showGridError.
    api.setGridOption('overlayNoRowsTemplate', DEFAULT_NO_ROWS_TEMPLATE)
    api.setGridOption('loading', true)
  }, [])

  // Mark the first-page fetch as done. `loading` is turned off synchronously so
  // showNoRowsOverlay() is not suppressed (no-rows is a no-op while loading).
  // Emptiness comes from the fetch itself so the error path can force no-rows
  // even when the row count is ambiguous.
  const finishGridLoading = useCallback((isEmpty: boolean, epoch: number) => {
    if (requestEpochRef.current !== epoch) return
    isLoadingRef.current = false
    hadErrorRef.current = false
    const api = gridApiRef.current
    if (!api) return
    // Restore the default no-rows template so a prior error message does not
    // linger (and leave the overlay blank) on a subsequent empty result.
    api.setGridOption('overlayNoRowsTemplate', DEFAULT_NO_ROWS_TEMPLATE)
    api.setGridOption('loading', false)
    if (isEmpty) api.showNoRowsOverlay()
    else api.hideOverlay()
  }, [])

  // Show an error overlay instead of the "No rows" message when a fetch fails.
  // Swaps the no-rows overlay template to an error message, then displays it.
  // The error flag is cleared on the next showGridLoading/finishGridLoading so
  // subsequent successful fetches restore the normal no-rows behavior.
  const showGridError = useCallback((message: string, epoch: number) => {
    if (requestEpochRef.current !== epoch) return
    isLoadingRef.current = false
    hadErrorRef.current = true
    const api = gridApiRef.current
    if (!api) return
    api.setGridOption('loading', false)
    api.setGridOption(
      'overlayNoRowsTemplate',
      `<span class="ag-grid-error-overlay">${message}</span>`
    )
    api.showNoRowsOverlay()
  }, [])

  // A background refresh (not the initial load) failed. AG Grid marks the
  // failed block's rows as unavailable, which drives getDisplayedRowCount()
  // to 0 and would make syncNoRowsOverlay() replace already-visible rows with
  // a misleading "No rows" overlay. Suppress that without touching the grid
  // (no loading/overlay changes) so existing rows stay on screen; the flag
  // clears on the next successful fetch via showGridLoading/finishGridLoading.
  const markBackgroundFetchError = useCallback((epoch: number) => {
    if (requestEpochRef.current !== epoch) return
    hadErrorRef.current = true
  }, [])

  // Re-apply the no-rows overlay after model updates. AG Grid's infinite row
  // model auto-hides the overlay on some model updates, so this restores it
  // from the displayed row count once no first-page fetch is in flight.
  const handleModelUpdated = useCallback(
    (event: ModelUpdatedEvent) => {
      if (isLoadingRef.current) return
      syncNoRowsOverlay(event.api)
    },
    [syncNoRowsOverlay]
  )

  return {
    gridApiRef,
    isMobile,
    isInitialLoad,
    setIsInitialLoad,
    beginRequestEpoch,
    getCurrentRequestEpoch,
    isCurrentRequestEpoch,
    showGridLoading,
    finishGridLoading,
    showGridError,
    markBackgroundFetchError,
    handleModelUpdated,
    initialState,
    resolvedColumnDefs,
    defaultColDef,
    handleGridReady,
    handleColumnChanged,
    handleColumnResized,
    handleSortChanged,
  }
}
